import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Card, Table } from "reactstrap";
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
} from "react-table";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import configure from "configure";
import Loader from "pages/Separate/Loader";
import NewPagination from "components/Common/Newpagination";
import { customGlobalFilter } from "utils/common-helpers";

function TableContainer({ columns, data, onReload }) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    state,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    gotoPage,
    setPageSize,
    pageOptions,
    setGlobalFilter,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      globalFilter: customGlobalFilter,
      initialState: { pageSize: 10 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );
  const { pageIndex, pageSize, globalFilter } = state;

  return (
    <div>
      <Row>
        <Col xl="12">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-0">
              <div className="text-purple fs-5 fw-bold">RFP Approvals</div>
              <div className="d-flex gap-3 align-items-center">
                <select
                  className="border-radius form-select"
                  style={{ width: "100px", height: "38px" }}
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {[10, 25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      Show {size}
                    </option>
                  ))}
                </select>
                <div className="cusrtom_search_box w-75">
                  <div className="search-box d-inline-block w-100">
                    <div className="position-relative">
                      <input
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        id="rfp-search"
                        type="text"
                        className="form-control rounded w-100 rounded-5"
                        style={{ height: "38px" }}
                        placeholder="Search..."
                        value={globalFilter || ""}
                      />
                      <i className="bx bx-search-alt search-icon"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="position-relative table-responsive">
              <Table className="table table-bordered mt-4" {...getTableProps()}>
                <thead className="fixed_view_payment_table">
                  {headerGroups.map((headerGroup, i) => (
                    <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column, index) => (
                        <th
                          key={index}
                          className={`t-col-${index + 1} ${
                            column.className || ""
                          }`}
                          {...column.getHeaderProps(
                            column.getSortByToggleProps(),
                          )}
                        >
                          <div className="d-flex align-items-center">
                            <span className="flex-grow-1 header-text">
                              {column.render("Header")}
                            </span>
                            {column.isShortIcon && (
                              <span className="d-flex align-items-center ms-2">
                                <i
                                  title="Ascending"
                                  className={`bi bi-arrow-up info-icon ${
                                    column.isSorted && !column.isSortedDesc
                                      ? "text-dark"
                                      : ""
                                  }`}
                                />
                                <i
                                  title="Descending"
                                  className={`bi bi-arrow-down info-icon ${
                                    column.isSorted && column.isSortedDesc
                                      ? "text-dark"
                                      : ""
                                  }`}
                                />
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody
                  className="fix-introducer-header"
                  {...getTableBodyProps()}
                >
                  {page.map((row, i) => {
                    prepareRow(row);
                    return (
                      <tr className="row-hover" key={i} {...row.getRowProps()}>
                        {row.cells.map((cell, index) => (
                          <td
                            key={index}
                            className={`t-col-${index + 1} ${
                              cell.column.className || ""
                            }`}
                            {...cell.getCellProps()}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            <Row className="justify-content-md-end justify-content-center align-items-center">
              <NewPagination
                gotoPage={gotoPage}
                canPreviousPage={canPreviousPage}
                previousPage={previousPage}
                canNextPage={canNextPage}
                pageIndex={pageIndex}
                pageOptions={pageOptions}
                pageSize={pageSize}
                page={page}
                data={data}
                nextPage={nextPage}
              />
            </Row>
          </div>
        </Col>
      </Row>
    </div>
  );
}

const RFPElement = () => {
  const [isLoading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const fetchRfpData = async () => {
    setLoading(true);
    try {
      const userId = localStorage.getItem("auth_id");
      const res = await axios.get(
        `${configure.nodeApi}/rfp_dashboard/${userId}`,
      );
      if (res.data.success) {
        setData(
          res.data.data.map((rfp) => ({
            ...rfp,
            name: rfp.display_name || "-",
            hours: rfp.timing_total_hrs || "-",
            cost:
              rfp.grand_total > 0
                ? `${rfp.currency_symbol}${rfp.grand_total}`
                : "-",
          })),
        );
      }
    } catch (err) {
      console.error("RFP dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRfpData();
  }, []);

  const onRfpApprove = async (rfpId, stepStatus) => {
    const s = stepStatus || {};
    let stepToApprove = null;
    let isFinalApproval = false;

    if (s.step2 === "submitted") {
      stepToApprove = 2;
    } else if (s.step3 === "submitted") {
      stepToApprove = 3;
    } else if (s.step4 === "done") {
      isFinalApproval = true;
    }

    if (!stepToApprove && !isFinalApproval) {
      Swal.fire({ icon: "info", text: "No pending step to approve." });
      return;
    }

    const stepLabels = { 2: "L1 (Features)", 3: "L2 (Timing)" };
    const label = isFinalApproval ? "Final RFP" : stepLabels[stepToApprove];

    const { isConfirmed } = await Swal.fire({
      title: `Approve ${label}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Approve",
      confirmButtonColor: "#752a97",
    });

    if (!isConfirmed) return;

    try {
      const formData = new FormData();
      formData.append("id", rfpId);
      formData.append("updated_by", localStorage.getItem("auth_id"));

      if (isFinalApproval) {
        formData.append("status", "approved");
        const res = await axios.post(
          `${configure.nodeApi}/update_rfp_status`,
          formData,
        );
        if (res.data.success) {
          Swal.fire({
            icon: "success",
            text: "RFP Final Approved!",
            timer: 1500,
            showConfirmButton: false,
          });
          fetchRfpData();
        } else {
          Swal.fire({ icon: "error", text: res.data.message });
        }
      } else {
        formData.append("step", stepToApprove);
        formData.append("user_id", localStorage.getItem("auth_id"));
        formData.append("status", "approved");
        const res = await axios.post(
          `${configure.nodeApi}/update_rfp_step_status`,
          formData,
        );
        if (res.data.success) {
          Swal.fire({
            icon: "success",
            text: `${label} approved!`,
            timer: 1500,
            showConfirmButton: false,
          });
          fetchRfpData();
        } else {
          Swal.fire({ icon: "error", text: res.data.message });
        }
      }
    } catch (err) {
      console.error("RFP approve error:", err);
      Swal.fire({ icon: "error", text: "Failed to approve" });
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "id",
        className: "col-md-1 text-center",
        Cell: ({ row }) => row.index + 1,
      },
      {
        Header: "Lead Id",
        accessor: "lead_id",
        className: "col-md-1 text-center",
      },
      {
        Header: "Date",
        accessor: "created_at",
        className: "col-md-1 text-center",
        isShortIcon: true,
        Cell: ({ value }) => moment(value).format("DD-MM-YYYY"),
      },
      {
        Header: "Name",
        accessor: "display_name",
        className: "col-md-4",
        isShortIcon: true,
      },
      {
        Header: "Hours",
        accessor: "hours",
        className: "text-center",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "Cost",
        accessor: "cost",
        className: "col-md-1 text-center",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "Progress",
        className: "col-md-2 text-center",
        Cell: ({ row }) => {
          const s = row.original.step_status || {};
          let pendingIndex = 0;
          if (s.step2 === "submitted" && s.step3 === "pending")
            pendingIndex = 0;
          else if (s.step3 === "submitted" && s.step4 === "pending")
            pendingIndex = 1;
          else if (s.step4 === "done") pendingIndex = 2;

          const steps = [
            { label: "Timing" },
            { label: "Costing" },
            { label: "Final" },
          ];
          return (
            <div className="d-flex flex-column align-items-center justify-content-center">
              <div className="d-flex align-items-center justify-content-center">
                {steps.map((step, i) => {
                  const isDone = pendingIndex > i;
                  const isPending = pendingIndex === i;
                  return (
                    <div key={step.label} className="d-flex align-items-center">
                      <div
                        title={step.label}
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          fontSize: "9px",
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: isDone
                            ? "#16A34A"
                            : isPending
                            ? "#F59E0B"
                            : "#E5E7EB",
                          color: isDone || isPending ? "#fff" : "#9CA3AF",
                          flexShrink: 0,
                        }}
                      >
                        {isDone ? (
                          <i
                            className="fas fa-check"
                            style={{ fontSize: "8px" }}
                          />
                        ) : (
                          i + 1
                        )}
                      </div>
                      {i < steps.length - 1 && (
                        <div
                          style={{
                            width: 40,
                            height: 2,
                            backgroundColor: isDone ? "#16A34A" : "#E5E7EB",
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="d-flex align-items-center justify-content-center mt-1">
                {steps.map((step, i) => (
                  <div key={step.label} className="d-flex align-items-center">
                    <div
                      style={{
                        textAlign: "center",
                        fontSize: "8px",
                        color: "#6B7280",
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                        minWidth: 10,
                      }}
                    >
                      {step.label}
                    </div>
                    {i < steps.length - 1 && <div style={{ width: 25 }} />}
                  </div>
                ))}
              </div>
            </div>
          );
        },
      },
      {
        Header: "Pending At",
        accessor: "step_status",
        className: "col-md-1 text-center",
        Cell: ({ value }) => {
          const s = value || {};
          let label = "-";
          if (s.step2 === "submitted" && s.step3 === "pending")
            label = "Timing";
          else if (s.step3 === "submitted" && s.step4 === "pending")
            label = "Costing";
          else if (
            s.step2 === "approved" &&
            s.step3 === "approved" &&
            s.step4 === "done"
          )
            label = "Final Approval";
          return <span className="badge bg-warning text-dark">{label}</span>;
        },
      },
      {
        Header: "Action",
        className: "col-md-1 text-center",
        Cell: ({ row }) => {
          const s = row.original.step_status || {};
          let stepToGo = 2;
          if (
            s.step2 === "approved" &&
            s.step3 === "approved" &&
            s.step4 === "done"
          )
            stepToGo = 5;
          else if (s.step3 === "submitted") stepToGo = 3;
          else if (s.step2 === "submitted") stepToGo = 2;

          return (
            <div className="d-flex">
              <a
                title="View RFP"
                className="btn btn-sm btn-outline-secondary me-1"
                href={`${configure.appUrl}request-for-proposal/${row.original.lead_id}?step=${stepToGo}`}
              >
                <i className="fas fa-eye"></i>
              </a>
              <a
                title="Approve"
                className="btn btn-sm btn-outline-primary me-1"
                onClick={() =>
                  onRfpApprove(row.original.id, row.original.step_status)
                }
              >
                <i className="bi bi-check-lg icon-center"></i>
              </a>
            </div>
          );
        },
      },
    ],
    [data],
  );

  return (
    <React.Fragment>
      {isLoading && <Loader />}
      <Card>
        <TableContainer columns={columns} data={data} onReload={fetchRfpData} />
      </Card>
    </React.Fragment>
  );
};

export default RFPElement;
